import React, { Component } from 'react';
import { Modal, Row, Col, Alert, Button } from 'antd';

import { calculateTotalAmount, calculateTotalServiceFee } from 'utils/transaction';
import { getCountriesConstant, getStatesConstant } from 'utils/apis/constants';
import { SPECIAL_HOST_IDS, VEEDU_HAUZ_HOST_ID } from 'utils/constants';
import { getReservationsById } from 'utils/apis/reservation';
import { withAppContext } from 'context/AppContext';

import TaxInvoice from './TaxInvoice';
import TaxReceipt from './TaxReceipt';
import TaxQuotation from './TaxQuotation';
import GuestRegistrationCard from './GuestRegistrationCard';
import RegistrationTicket from './RegistrationTicket';
import DepositRefunded from './RefundableDeposit';
import DepositReceipt from './DepositReceipt';
import intl from 'react-intl-universal';
import Receipt from './Receipt';
import { guard } from 'utils/general';

class PdfCardModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      countries: [],
      states: [],
      reservation: {},
      geoConstantsReady: false
    };
    /** Cached PDF header data — stable reference unless reservation or geo inputs change (avoids resetting react-pdf BlobProvider on every AppContext re-render). */
    this._pdfPartialKey = null;
    this._pdfPartialData = null;
  }

  loadGeoConstants = async () => {
    try {
      const [countries, states] = await Promise.all([getCountriesConstant(), getStatesConstant('MY')]);
      this.setState({
        countries: Object.values(countries || {}),
        states: Object.values(states || {}),
        geoConstantsReady: true
      });
    } catch (e) {
      this.setState({ countries: [], states: [], geoConstantsReady: true });
    }
  };

  loadReservation = async () => {
    const { reservationId } = this.props;
    const reservation = await getReservationsById(reservationId, { isWithTransaction: true });
    const reservationWithTotalAmountAndServiceFee = {
      ...reservation.data,
      totalPrice: calculateTotalAmount(reservation.data.charges, reservation.data.taxes),
      totalServiceFee: calculateTotalServiceFee(reservation.data.charges)
    };
    this.setState({
      reservation: reservationWithTotalAmountAndServiceFee
    });
  };

  getPaymentDetails = (payment = {}) => {
    let methodsUsed = [];
    let remarks = [];
    let cardNos = [];
    let expiryDates = [];
    if (payment && payment.details) {
      payment.details.forEach(detail => {
        if (!methodsUsed.includes(detail.method)) {
          methodsUsed.push(detail.method);
        }
        remarks.push(detail.remarks);
        if (!cardNos.includes(detail.cardNo)) {
          cardNos.push(detail.cardNo);
          expiryDates.push(detail.expiryDate);
        }
      });
    }
    return {
      methods: methodsUsed.length > 0 ? methodsUsed.filter(method => !!method).join(', ') : '',
      remarks: remarks.length > 0 ? remarks.filter(remark => !!remark).join('. ') : '',
      cardNos: cardNos.length > 0 ? cardNos.filter(no => !!no).join(', ') : '',
      expiryDates: expiryDates.length > 0 ? expiryDates.filter(date => !!date).join(', ') : ''
    };
  };

  getGuestNationality = nationality => {
    const { countries } = this.state;
    const filteredCountries = countries.filter(country => country.iso3 === nationality || country.iso2 === nationality);
    if (filteredCountries.length > 0) {
      return filteredCountries[0].name;
    }
    return '';
  };

  componentDidMount = () => {
    this.loadGeoConstants();
    this.loadReservation();
  };

  formatDataForPdf = reservation => {
    const { countries, states } = this.state;
    const taxNo =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property && reservation.unit.roomType.property.host
        ? reservation.unit.roomType.property.host.taxNo
        : '';
    const image =
      reservation &&
      reservation.unit &&
      reservation.unit.roomType &&
      reservation.unit.roomType.property &&
      reservation.unit.roomType.property.host &&
      reservation.unit.roomType.property.host.images &&
      reservation.unit.roomType.property.host.images[0]
        ? reservation.unit.roomType.property.host.images[0].imageUrl
        : 'https:dashboard-operator-image.s3.amazonaws.com/3386bffc-75c9-4390-a670-fc11ed8febe1_blanksquare.png';
    const city =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property
        ? reservation.unit.roomType.property.city
        : '';
    const street =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property
        ? reservation.unit.roomType.property.street
        : '';
    const zipCode =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property
        ? reservation.unit.roomType.property.zipCode
        : '';
    const user = reservation.createdBy ? reservation.createdBy.userProfile : {};
    let countryCode =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property
        ? reservation.unit.roomType.property.countryCode
        : '';
    countryCode = countryCode ? countries.filter(c => c.iso3 === countryCode || c.iso2 === countryCode)[0] : '';
    const country = countryCode ? countryCode.name : '';
    let state =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property
        ? reservation.unit.roomType.property.state
        : '';
    state = state && states.filter(a => a.code === state)[0] ? states.filter(a => a.code === state)[0].label : '';

    const partialData = {
      _id: reservation._id || '',
      confirmationCode: reservation.code || '',
      finePrint: reservation.unit && reservation.unit.finePrint,
      taxNo,
      image,
      city,
      street,
      zipCode,
      createdBy: user,
      country
    };

    return partialData;
  };

  render() {
    const { visible, handleClose, currency, host, checkIsAdmin, openCreateEinvoiceModal, isCreateEinvoiceExist, user } = this.props;

    const { reservation } = this.state;
    const effectiveCurrency = currency || reservation.currency;

    // needed for when host can't be passed in as prop (such as Reservation.js), for feature-gating
    const hostIdFromReservation =
      reservation && reservation.unit && reservation.unit.roomType && reservation.unit.roomType.property && reservation.unit.roomType.property.host
        ? reservation.unit.roomType.property.host._id
        : '';

    if (!reservation._id) {
      return null;
    }
    if (!this.state.geoConstantsReady) {
      return (
        <Modal
          title={`${intl.get('reservations.csvModal.titleCode').d('Receipts for Confirmation Code :  ')}${reservation.code}`}
          visible={visible}
          footer={null}
          onCancel={handleClose}
          destroyOnClose={true}
          closable={true}
        >
          <div style={{ padding: 40, textAlign: 'center' }}>{intl.get('invoiceList.pdfModal.loading').d('Loading...')}</div>
        </Modal>
      );
    }

    const cacheKey = [
      reservation._id,
      reservation.code,
      reservation.invoiceNumber,
      reservation.totalPrice,
      this.state.countries.length,
      this.state.states.length
    ].join('|');
    if (this._pdfPartialKey !== cacheKey) {
      this._pdfPartialData = this.formatDataForPdf(reservation);
      this._pdfPartialKey = cacheKey;
    }
    const partialData = this._pdfPartialData;
    return (
      <Modal
        title={`${intl.get('reservations.csvModal.titleCode').d('Receipts for Confirmation Code :  ')}${reservation.code}`}
        visible={visible}
        footer={null}
        onCancel={handleClose}
        destroyOnClose={true}
        closable={true}
      >
        <Alert
          message={intl.get('reservations.csvModal.message').d('Info')}
          description={intl.get('reservations.csvModal.desc').d("Please disable Adblocker if receipts can't be opened.")}
          type="warning"
          showIcon
        />
        <Row type="flex" justify="start" gutter={8}>
          <Col className="modal-receipts-col" sm={12} xs={24}>
            <TaxQuotation data={partialData} reservation={reservation} getGuestNationality={this.getGuestNationality} currency={effectiveCurrency} />
          </Col>

          <Col className="modal-receipts-col" sm={12} xs={24}>
            <TaxInvoice
              data={partialData}
              reservation={reservation}
              getGuestNationality={this.getGuestNationality}
              currency={effectiveCurrency}
              host={host}
            />
          </Col>
          {/* )} */}

          <Col className="modal-receipts-col" sm={12} xs={24}>
            <GuestRegistrationCard
              data={partialData}
              reservation={reservation}
              getPaymentDetails={this.getPaymentDetails}
              getGuestNationality={this.getGuestNationality}
              currency={effectiveCurrency}
            />
          </Col>

          <Col className="modal-receipts-col" sm={12} xs={24}>
            {checkIsAdmin() || SPECIAL_HOST_IDS.includes(host) ? (
              <TaxReceipt
                data={partialData}
                reservation={reservation}
                getGuestNationality={this.getGuestNationality}
                currency={effectiveCurrency}
                identificationNo={guard(() => reservation.guestDetails.userProfile.identificationNo, '-')}
                host={host}
                isSpecialHost={SPECIAL_HOST_IDS.includes(host)}
              />
            ) : (
              <Receipt
                data={partialData}
                reservation={reservation}
                getGuestNationality={this.getGuestNationality}
                currency={effectiveCurrency}
                identificationNo={guard(() => reservation.guestDetails.userProfile.identificationNo, '-')}
                host={host}
                isSpecialHost={false}
              />
            )}
          </Col>
          {/* VEEDU HAUZ SPECIAL REQUEST */}
          {(host === VEEDU_HAUZ_HOST_ID || hostIdFromReservation === VEEDU_HAUZ_HOST_ID) && (
            <>
              <Col className="modal-receipts-col" sm={12} xs={24}>
                <RegistrationTicket
                  data={partialData}
                  reservation={reservation}
                  getPaymentDetails={this.getPaymentDetails}
                  getGuestNationality={this.getGuestNationality}
                  currency={effectiveCurrency}
                />
              </Col>
              <Col className="modal-receipts-col" sm={12} xs={24}>
                <DepositReceipt data={partialData} reservation={reservation} currency={effectiveCurrency} />
              </Col>
            </>
          )}
          <Col className="modal-receipts-col" sm={12} xs={24}>
            <DepositRefunded data={partialData} reservation={reservation} currency={effectiveCurrency} />
          </Col>

          {isCreateEinvoiceExist && (
            <Col className="modal-receipts-col" sm={12} xs={24}>
              <Button
                type="primary"
                className="modal-receipts-btn"
                disabled={user.isAdmin || user.roles[0].permissions.includes(83) ? false : true}
                loading={guard(
                  () => !(this.props.invoiceDescription.roomTypeOptions.length > 0 && this.props.invoiceDescription.unitOptions.length > 0),
                  true
                )}
                onClick={() => {
                  handleClose();

                  setTimeout(() => {
                    openCreateEinvoiceModal();
                  }, 500);
                }}
              >
                Create e-Invoice
              </Button>
            </Col>
          )}

          {/* <Col className="modal-receipts-col" sm={12} xs={24}>
            <Receipt data={partialData} reservation={reservation} getGuestNationality={this.getGuestNationality} />
          </Col> */}
        </Row>
      </Modal>
    );
  }
}

export default withAppContext(PdfCardModal);
